// AdminReadAuditPage — /admin/read-audit：「某资源的读取记录」（SB-116，第一百二十一批）。
//
// ── 这一页回答的问题 ────────────────────────────────────────────────────────
// 「谁读了这个资源」——按资源标识查主体审计（组间线、密递、账号名册、隐藏文档）。
// 第一百零八批那次排查（外包读没读过组间线）的结论只能写成「既不能证明也不能
// 排除」；后端的 read_audit 表把答案存下来了，而这一页是它唯一的人类读法：
// 审计存了但查不到，等于没存（工单原话，DP-6 类：设计在、通道缺）。
//
// ── 两条呈现纪律 ────────────────────────────────────────────────────────────
// ① **denied 与 allowed 都在表里**：被拒的那次同样是情报（"谁在试"）。顶部的
//    读者名单只数 allowed（"内容给谁看过"），试探名单另列——两个问题两份名单，
//    揉在一起哪个都答不了。
// ② **查询是显式动作**（按钮，不是边打字边查）：resource 必填，一次查询对应
//    一次可复述的取证动作（"我查了 group-thread:7 在这个窗内的记录"）。
//
// 判据（参数拼装、行归一化、名单去重、本地校验）全在 pages/readAudit.mjs 配单测；
// 这里只渲染（SB-042：`.jsx` 里不放判据）。
import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import AdminSubnav from '../components/AdminSubnav.jsx';
import DataTable from '../components/DataTable.jsx';
import HelpLink from '../components/HelpLink.jsx';
import { fmtTime } from '../components/TicketDetailView.jsx';
import { useLoad } from '../hooks/useLoad.mjs';
import { useLang, useT } from '../i18n/index.mjs';
import {
  buildReadAuditQuery, credentialKey, distinctDeniedProbers, distinctReaders,
  filterProblems, normalizeReadAudit, readAuditLimitNote, readerLabel, verdictKey,
} from './readAudit.mjs';

export default function AdminReadAuditPage() {
  const t = useT();
  const { lang } = useLang();
  const [form, setForm] = useState({ resource: '', from: '', to: '' });
  // applied = 已按下「查询」的那份过滤器。null = 还没查过（初始不发请求——
  // 这条端点没有"默认全量"，resource 缺席在后端就是 400）。
  const [applied, setApplied] = useState(null);

  const problems = filterProblems(form);
  const load = useLoad(
    () => (applied ? api.adminReadAudit(buildReadAuditQuery(applied)) : Promise.resolve(null)),
    [applied],
  );
  const rows = useMemo(() => normalizeReadAudit(load.data), [load.data]);
  const readers = useMemo(() => distinctReaders(rows), [rows]);
  const probers = useMemo(() => distinctDeniedProbers(rows), [rows]);
  // SB-159（第三百八十二批）：撞上上限 ⇒ 名单只是这一页的（见 readAudit.mjs 那段 ⚠）。
  const limitNote = load.status === 'ready' ? readAuditLimitNote(load.data) : null;
  const pageOnly = limitNote ? t('admin.readAudit.pageOnly') : '';

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Box>
      <AdminSubnav />
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
        {t('admin.readAudit.title')}
        <HelpLink section="admin-audit" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('admin.readAudit.desc')}
      </Typography>

      <Stack direction="row" spacing={2} useFlexGap
             sx={{ alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
        <TextField
          size="small"
          label={t('admin.readAudit.resource')}
          /* helperText 常驻举例：资源标识是内部记法（group-thread:7 / admin:accounts /
             doc:<组>/<docKey>），不给例子等于要人背台账的键格式。 */
          helperText={t('admin.readAudit.resourceHint')}
          value={form.resource}
          onChange={set('resource')}
          sx={{ minWidth: 280 }}
        />
        <TextField
          size="small"
          type="datetime-local"
          label={t('admin.readAudit.from')}
          InputLabelProps={{ shrink: true }}
          value={form.from}
          onChange={set('from')}
        />
        <TextField
          size="small"
          type="datetime-local"
          label={t('admin.readAudit.to')}
          InputLabelProps={{ shrink: true }}
          value={form.to}
          onChange={set('to')}
        />
        <Button
          variant="contained"
          disabled={problems.length > 0}
          onClick={() => setApplied({ ...form })}
        >
          {t('admin.readAudit.query')}
        </Button>
      </Stack>
      {/* 起止写反是本地就能判定的错——不必往返服务端；resource 缺席只禁用按钮，
          不弹错（还没填完不是错）。 */}
      {problems.includes('admin.readAudit.err.range') ? (
        <Alert severity="warning" sx={{ mb: 2 }}>{t('admin.readAudit.err.range')}</Alert>
      ) : null}

      {applied === null ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('admin.readAudit.prompt')}</Typography>
        </Paper>
      ) : load.status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : load.status === 'error' ? (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>
            {t(errKey(load.error, { fallback: 'common.error.network' }))}
          </Alert>
          <Button size="small" onClick={load.reload}>{t('common.retry')}</Button>
        </Box>
      ) : rows.length === 0 ? (
        // 空结果如实说"这个窗内没有记录"——它不等于"没人读过"（窗可能不对、
        // 保留期（180 天）之外的行已被清理），这句话在文案里说清。
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('admin.readAudit.empty')}</Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {limitNote ? (
            <Alert severity="warning" data-paging-notice="admin.readAudit.limitReached">
              {t('admin.readAudit.limitReached', {
                n: limitNote.n ?? rows.length,
                oldest: limitNote.oldestAtMs ? fmtTime(limitNote.oldestAtMs, lang) : '—',
              })}
            </Alert>
          ) : null}
          {/* 两份名单，两个问题：readers=「内容给谁看过」（验收 §3 ② 的那份唯一
              名单），probers=「谁在试」。撞上限时两份都只是这一页的，标「仅本页」。 */}
          <Typography variant="body2">
            {t('admin.readAudit.readers', { list: readers.join('、') || '—' })}{pageOnly}
            {probers.length > 0
              ? ` ${t('admin.readAudit.probers', { list: probers.join('、') })}${pageOnly}`
              : ''}
          </Typography>
          <DataTable minWidth={760}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.readAudit.col.at')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.readAudit.col.who')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.readAudit.col.verdict')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.readAudit.col.credential')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.readAudit.col.surface')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const vk = verdictKey(row.verdict);
                const ck = credentialKey(row.credential);
                return (
                  <TableRow key={row.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTime(row.atMs, lang)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Stack spacing={0.25}>
                        <Typography variant="body2">{readerLabel(row)}</Typography>
                        {row.email && row.displayName ? (
                          <Typography variant="caption" color="text.secondary">
                            {row.email}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {/* 值域外 ⇒ 渲染原串（不硬套标签：替台账说话比奇怪的词更坏）。 */}
                      {vk ? (
                        <Chip size="small" variant="outlined"
                              color={row.verdict === 'allowed' ? 'success' : 'error'}
                              label={t(vk)} />
                      ) : (
                        <Typography variant="body2">{row.verdict}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {ck ? t(ck) : row.credential}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap',
                                     fontFamily: 'ui-monospace, monospace' }}>
                      {row.surface}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </DataTable>
        </Stack>
      )}
    </Box>
  );
}
